import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

// Models
import { MetaData, ResponseData } from 'src/app/platform/models/utils';
import { AccountData, AddressData, PhoneData, User, UserBalance, UserDebit } from 'src/app/modules/account/models/user';

// Services
import { ApiService } from 'src/app/platform/services/api.service';
import { DeviceService } from 'src/app/platform/services/device.service';
import { PlatformService } from 'src/app/platform/services/platform.service';
import { LocalStorageService } from 'src/app/platform/services/local-storage.service';

/**
 * This class is in charge of managing data regarding users.
 */
@Injectable()
export class UserService {
    public currentUser = new BehaviorSubject<User>(this.getCurrentUser());

    constructor(
        private apiService: ApiService,
        private deviceService: DeviceService,
        private platformService: PlatformService,
        private localStorageService: LocalStorageService,
    ) { }

    /**
     * Call the api to authenticate the user
     */
    login(user: string, password: string): Promise<any> {
        const headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': ''
        };

        const loginData = {
            Username: user,
            Password: password,
        };

        const loginPromise = new Promise((resolve, reject) => {
            this.apiService.request('post', 'usuarios/login', loginData, headers).toPromise().then((resultLogin: any) => {
                if (resultLogin.response.status) {
                    headers.Authorization = 'Bearer ' + resultLogin.token;

                    const userPromise = new Promise((resolve, reject) => {
                        this.apiService.request('get', 'usuarios/' + resultLogin.id, {}, headers).toPromise().then((resultUser: any) => {
                            const userData = new User();

                            if (resultUser.response.status) {
                                userData.configData({
                                    id: resultUser.id,
                                    token: resultLogin.token,
                                    username: user,
                                    accounts: userData.configAccounts(resultUser.cuentas),
                                    rol: resultUser.rol
                                });

                                this.update(userData);
                                this.deviceService.notificationsSetUser(userData.id);

                                resolve(this.apiService.successDataPrepare(resultUser.response.message, resultUser));
                            } else {
                                reject(this.apiService.errorDataPrepare(resultUser.response.message, resultUser));
                            }
                        }, (error) => {
                            this.localStorageService.removeUser();
                            this.deviceService.notificationsRemoveUser();
                            reject(this.apiService.errorDataPrepare(error.response.message, error));
                        });
                    });

                    resolve(userPromise);
                } else {
                    reject(this.apiService.errorDataPrepare(resultLogin.response.message, resultLogin));
                }
            }, (error) => {
                this.localStorageService.removeUser();
                reject(this.apiService.errorDataPrepare(error.response.message, error));
            });
        });

        return loginPromise;
    }

    /**
     * Closes the current session.
     */
    logout(): void {
        this.currentUser.next(new User());
        this.localStorageService.removeUser();
        this.localStorageService.removeAccount();
        this.deviceService.notificationsRemoveUser();
    }

    /**
     * Call the api to reset a password that sends a confirmation email to the user
     */
    recover(email: string): Promise<ResponseData> {
        return new Promise((resolve, reject) => {
            const headers = {
                'Content-Type': 'application/x-www-form-urlencoded',
            };

            this.apiService.request('post', 'usuarios/PasswordRecovery?username=' + email, {}, headers).toPromise().then((success: any) => {
                if (success.status) {
                    resolve(this.apiService.successDataPrepare(success.message, success));
                } else {
                    reject(this.apiService.errorDataPrepare(success.message, success));
                }
            }, (error) => {
                reject(this.apiService.errorDataPrepare(error.message, error));
            });
        });
    }

    /**
     * Call the api to generate a new password for the user and send it by email
     */
    restore(hash: string): Promise<ResponseData> {
        return new Promise((resolve, reject) => {
            const headers = {
                'Content-Type': 'application/x-www-form-urlencoded'
            };

            this.apiService.request('post', 'usuarios/passwordgenerate?token=' + hash, {}, headers).toPromise().then((success: any) => {
                if (success.status) {
                    resolve(this.apiService.successDataPrepare(success.message, success));
                } else {
                    reject(this.apiService.errorDataPrepare(success.message, success));
                }
            }, (error) => {
                reject(this.apiService.errorDataPrepare(error.message, error));
            });
        });
    }
    /**
     * Call the api to search accounts
     */
    accounts(query: string): Promise<ResponseData> {
        return new Promise((resolve, reject) => {
            const headers = {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Authorization': 'Bearer ' + this.currentUser.value.token
            };

            this.apiService.request('get', 'usuarios/admin/' + this.currentUser.value.id + '?clienteId=' + query, {}, headers).toPromise().then((success: any) => {
                if (success.response.status) {
                    const data: Array<AccountData> = [];

                    success.cuentas.forEach((value: any) => {
                        data.push(new AccountData(value));
                    });
                    
                    resolve(this.apiService.successDataPrepare(success.response.message, data));
                } else {
                    reject(this.apiService.errorDataPrepare(success.message, success));
                }
            }, (error) => {
                reject(this.apiService.errorDataPrepare(error.message, error));
            });
        });
    }

    /**
     * Updates the data regarding the current user.
     */
    update(user: User): void {
        user = Object.assign(new User(), user);

        this.localStorageService.setUser(user);
        this.currentUser.next(user);
    }

    /**
     * Updates the data off the current user.
     */
    updateData(data: string, value: any): void {
        const user = this.currentUser.value;

        (user as any)[data] = value;

        this.localStorageService.setUser(user);
        this.currentUser.next(user);
    }

    /**
     * Verifies whether there is a user logged in or not.
     */
    isLogged(): boolean {
        if (this.currentUser && this.currentUser.value && this.currentUser.value.isValid()) {
            return true;
        }

        return false;
    }

    /**
     * Updates user password
     */
    userPassUpdate(passwordNew: string, passwordCurrent: string): Promise<ResponseData> {
        return new Promise((resolve, reject) => {
            const headers = {
              'Content-Type': 'application/x-www-form-urlencoded',
              'Authorization': 'Bearer ' + this.currentUser.value.token
            };

            const data = {
                token: this.currentUser.value.token,
                newPassword: passwordNew,
                oldPassword: passwordCurrent
            };

            this.apiService.request('post', 'usuarios/' + this.currentUser.value.id + '/passwordchange', data, headers).toPromise().then((success: any) => {
              if (success.status) {
                resolve(this.apiService.successDataPrepare('', success));
              } else {
                resolve(this.apiService.errorDataPrepare(success.message, success));
              }
            }, (error) => {
              reject(this.apiService.errorDataPrepare(error.message, error));
            });
        });
    }

    /**
     * Reads the current user from the local storage.
     */
     private getCurrentUser(): User {
        return this.localStorageService.getUser();
    }

    dgoLogin(Username: string, password: string, data: any = {}): Promise<ResponseData> {
        return new Promise((resolve, reject) => {
            this.apiService.request(
                'post',
                '/directv/api/v1/oauth/login',
                { ...{ Username, password }, ...data },
                { 'Content-Type': 'application/x-www-form-urlencoded', },
                'https://developer.iptel.com.ar'
            ).toPromise().then((success: any) => {
                if(success.status.success){
                    resolve(this.apiService.successDataPrepare(success.status.message, success.data.redirect_uri));
                } else {
                    resolve(this.apiService.errorDataPrepare(success.status.message, success.data.redirect_uri ?? ''));
                }
            }).catch((error) => {
                reject(this.apiService.errorDataPrepare(error.body.status.message, error.body));
            });
        });
    }
}
